import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";

type FeatureFlagState = boolean | string;

/**
 * FeatureFlagsStateService
 * --------------------------------------------------------------------
 * Centralised store for per-feature "is this feature exposed by the
 * currently-connected device" flags (formerly the `*FeatApi`
 * BehaviorSubjects on `ApiService`) plus schema versions for the
 * features that carry one.
 *
 * NOTE on typing: kept permissive to match the legacy untyped
 * `ApiService` contract — some call-sites push non-boolean truthy
 * values into these flags. Tightening is a follow-up task.
 */
@Injectable({ providedIn: "root" })
export class FeatureFlagsStateService {
  // --- Boolean availability flags (one per feature) -----------------
  readonly aloFeatApi = new BehaviorSubject<FeatureFlagState>(false);
  readonly getAloFeatApi: Observable<FeatureFlagState> = this.aloFeatApi.asObservable();

  readonly aocFeatApi = new BehaviorSubject<FeatureFlagState>(false);
  readonly getAocFeatApi: Observable<FeatureFlagState> = this.aocFeatApi.asObservable();

  readonly cloFeatApi = new BehaviorSubject<FeatureFlagState>(false);
  readonly getCloFeatApi: Observable<FeatureFlagState> = this.cloFeatApi.asObservable();

  readonly cloLiteFeatApi = new BehaviorSubject<FeatureFlagState>(false);
  readonly getCloLiteFeatApi: Observable<FeatureFlagState> = this.cloLiteFeatApi.asObservable();

  readonly codedMainsFeatApi = new BehaviorSubject<FeatureFlagState>(false);
  readonly getCodedMainsFeatApi: Observable<FeatureFlagState> = this.codedMainsFeatApi.asObservable();

  readonly lineSwicthFeatApi = new BehaviorSubject<FeatureFlagState>(false);
  readonly getLineSwitchFeatApi: Observable<FeatureFlagState> = this.lineSwicthFeatApi.asObservable();

  readonly owpFeatApi = new BehaviorSubject<FeatureFlagState>(false);
  readonly getOwpFeatApi: Observable<FeatureFlagState> = this.owpFeatApi.asObservable();

  readonly dimmingInterfaceFeatApi = new BehaviorSubject<FeatureFlagState>(false);
  readonly getDimmingInterfaceFeatApi: Observable<FeatureFlagState> =
    this.dimmingInterfaceFeatApi.asObservable();

  readonly daliPSUFeatApi = new BehaviorSubject<FeatureFlagState>(false);
  readonly getDaliPSUFeatApi: Observable<FeatureFlagState> = this.daliPSUFeatApi.asObservable();

  readonly ztvFeatApi = new BehaviorSubject<FeatureFlagState>(false);
  readonly getZtvFeatApi: Observable<FeatureFlagState> = this.ztvFeatApi.asObservable();

  readonly ztvoutputFeatApi = new BehaviorSubject<FeatureFlagState>(false);
  readonly getztvoutputFeatApi: Observable<FeatureFlagState> = this.ztvoutputFeatApi.asObservable();

  readonly rsoFeatApi = new BehaviorSubject<FeatureFlagState>(false);
  readonly getrsoFeatApi: Observable<FeatureFlagState> = this.rsoFeatApi.asObservable();

  readonly lipFeatApi = new BehaviorSubject<FeatureFlagState>(false);
  readonly getlipFeatApi: Observable<FeatureFlagState> = this.lipFeatApi.asObservable();

  readonly lumininfoformatFeatApi = new BehaviorSubject<FeatureFlagState>(false);
  readonly getLumininfoformatFeatApi: Observable<FeatureFlagState> =
    this.lumininfoformatFeatApi.asObservable();

  readonly startUpTimeFeatApi = new BehaviorSubject<FeatureFlagState>(false);
  readonly getStartUpTimeFeatApi: Observable<FeatureFlagState> = this.startUpTimeFeatApi.asObservable();

  readonly dtlFeatApi = new BehaviorSubject<FeatureFlagState>(false);
  readonly getDtlFeatApi: Observable<FeatureFlagState> = this.dtlFeatApi.asObservable();

  readonly corridorFeatApi = new BehaviorSubject<FeatureFlagState>(false);
  readonly getCorridoeFeatApi: Observable<FeatureFlagState> = this.corridorFeatApi.asObservable();

  readonly mtpFeatApi = new BehaviorSubject<FeatureFlagState>(false);
  readonly getMtpFeatApi: Observable<FeatureFlagState> = this.mtpFeatApi.asObservable();

  readonly ampDimFeatApi = new BehaviorSubject<FeatureFlagState>(false);
  readonly getampDimFeatApi: Observable<FeatureFlagState> = this.ampDimFeatApi.asObservable();

  readonly dcEmergencyFeatApi = new BehaviorSubject<FeatureFlagState>(false);
  readonly getDCEmergencyFeatApi: Observable<FeatureFlagState> = this.dcEmergencyFeatApi.asObservable();

  readonly minDimLevelFeatApi = new BehaviorSubject<FeatureFlagState>(false);
  readonly getminDimLevelFeatApi: Observable<FeatureFlagState> = this.minDimLevelFeatApi.asObservable();

  readonly touchDimFeatApi = new BehaviorSubject<FeatureFlagState>(false);
  readonly getTouchDimFeatApi: Observable<FeatureFlagState> = this.touchDimFeatApi.asObservable();

  readonly diia253maintenanceFeatApi = new BehaviorSubject<FeatureFlagState>(false);
  readonly getdiia253maintenanceFeatApi: Observable<FeatureFlagState> =
    this.diia253maintenanceFeatApi.asObservable();

  readonly dynaDimmerFeatApi = new BehaviorSubject<FeatureFlagState>(false);
  readonly getDynaDimmerFeatApi: Observable<FeatureFlagState> = this.dynaDimmerFeatApi.asObservable();

  readonly multiconfigFeatApi = new BehaviorSubject<FeatureFlagState>(false);
  readonly getmulticonfigApiFeatApi: Observable<FeatureFlagState> = this.multiconfigFeatApi.asObservable();

  readonly dali102AddressingFeatApi = new BehaviorSubject<FeatureFlagState>(false);
  readonly getDali102AddressingFeatApi: Observable<FeatureFlagState> =
    this.dali102AddressingFeatApi.asObservable();

  readonly dali102and207ConfigurationFeatApi = new BehaviorSubject<FeatureFlagState>(false);
  readonly getDali102and207ConfigurationFeatApi: Observable<FeatureFlagState> =
    this.dali102and207ConfigurationFeatApi.asObservable();

  readonly auxFeatApi = new BehaviorSubject<FeatureFlagState>(false);
  readonly getAuxFeatApi: Observable<FeatureFlagState> = this.auxFeatApi.asObservable();

  readonly lsiFeatApi = new BehaviorSubject<FeatureFlagState>(false);
  readonly getLsiFeatApi: Observable<FeatureFlagState> = this.lsiFeatApi.asObservable();

  readonly aocmultichannelFeatApi = new BehaviorSubject<FeatureFlagState>(false);
  readonly getAocMultiChannelFeatApi: Observable<FeatureFlagState> =
    this.aocmultichannelFeatApi.asObservable();

  readonly flextuneFeatApi = new BehaviorSubject<FeatureFlagState>(false);
  readonly getFlextuneFeatApi: Observable<FeatureFlagState> = this.flextuneFeatApi.asObservable();

  readonly otpFeatApi = new BehaviorSubject<FeatureFlagState>(false);
  readonly getOtpFeatApi: Observable<FeatureFlagState> = this.otpFeatApi.asObservable();

  readonly dscFeatApi = new BehaviorSubject<FeatureFlagState>(false);
  readonly getDscFeatApi: Observable<FeatureFlagState> = this.dscFeatApi.asObservable();

  readonly lowmainslockFeatApi = new BehaviorSubject<FeatureFlagState>(false);
  readonly getLowMainsLockFeatApi: Observable<FeatureFlagState> = this.lowmainslockFeatApi.asObservable();

  // --- Schema versions (string per feature) --------------------------
  readonly dali102AddressingSchemaVersion = new BehaviorSubject<string>("");
  readonly getDali102AddressingSchemaVersion: Observable<string> =
    this.dali102AddressingSchemaVersion.asObservable();

  readonly dali102and207ConfigurationSchemaVersion = new BehaviorSubject<string>("");
  readonly getDali102and207ConfigurationSchemaVersion: Observable<string> =
    this.dali102and207ConfigurationSchemaVersion.asObservable();

  readonly auxSchemaVersion = new BehaviorSubject<string>("");
  readonly getAuxSchemaVersion: Observable<string> = this.auxSchemaVersion.asObservable();

  readonly lsiSchemaVersion = new BehaviorSubject<string>("");
  readonly getLsiSchemaVersion: Observable<string> = this.lsiSchemaVersion.asObservable();

  readonly aocmultichannelSchemaVersion = new BehaviorSubject<string>("");
  readonly getAocMultiChannelSchemaVersion: Observable<string> =
    this.aocmultichannelSchemaVersion.asObservable();

  readonly otpSchemaVersion = new BehaviorSubject<string>("");
  readonly getOtpSchemaVersion: Observable<string> = this.otpSchemaVersion.asObservable();

  readonly dscSchemaVersion = new BehaviorSubject<string>("");
  readonly getDscSchemaVersion: Observable<string> = this.dscSchemaVersion.asObservable();

  readonly lowmainslockSchemaVersion = new BehaviorSubject<string>("");
  readonly getLowMainsLockSchemaVersion: Observable<string> = this.lowmainslockSchemaVersion.asObservable();

  // --- Setters (names match the legacy ApiService API) ---------------
  setAloAPI(data: FeatureFlagState): void {
    this.aloFeatApi.next(data);
  }
  setAocAPI(data: FeatureFlagState): void {
    this.aocFeatApi.next(data);
  }
  setCloAPI(data: FeatureFlagState): void {
    this.cloFeatApi.next(data);
  }
  setCloLiteAPI(data: FeatureFlagState): void {
    this.cloLiteFeatApi.next(data);
  }
  setCodedMainsAPI(data: FeatureFlagState): void {
    this.codedMainsFeatApi.next(data);
  }
  setLineSwitchAPI(data: FeatureFlagState): void {
    this.lineSwicthFeatApi.next(data);
  }
  setowpAPI(data: FeatureFlagState): void {
    this.owpFeatApi.next(data);
  }
  setDimmingInterfaceAPI(data: FeatureFlagState): void {
    this.dimmingInterfaceFeatApi.next(data);
  }
  setMulticonfigAPI(data: FeatureFlagState): void {
    this.multiconfigFeatApi.next(data);
  }
  setDaliPSUAPI(data: FeatureFlagState): void {
    this.daliPSUFeatApi.next(data);
  }
  setZtvAPI(data: FeatureFlagState): void {
    this.ztvFeatApi.next(data);
  }
  setztvoutputAPI(data: FeatureFlagState): void {
    this.ztvoutputFeatApi.next(data);
  }
  setrsoAPI(data: FeatureFlagState): void {
    this.rsoFeatApi.next(data);
  }
  setlipAPI(data: FeatureFlagState): void {
    this.lipFeatApi.next(data);
  }
  setLuminAPI(data: FeatureFlagState): void {
    this.lumininfoformatFeatApi.next(data);
  }
  setStartUpTimeAPI(data: FeatureFlagState): void {
    this.startUpTimeFeatApi.next(data);
  }
  setDtlAPI(data: FeatureFlagState): void {
    this.dtlFeatApi.next(data);
  }
  setFlextuneAPI(data: FeatureFlagState): void {
    this.flextuneFeatApi.next(data);
  }
  setCorridorAPI(data: FeatureFlagState): void {
    this.corridorFeatApi.next(data);
  }
  setMtpAPI(data: FeatureFlagState): void {
    this.mtpFeatApi.next(data);
  }
  setAmpDimAPI(data: FeatureFlagState): void {
    this.ampDimFeatApi.next(data);
  }
  setDCEmeregncyAPI(data: FeatureFlagState): void {
    this.dcEmergencyFeatApi.next(data);
  }
  setMinDimLevelAPI(data: FeatureFlagState): void {
    this.minDimLevelFeatApi.next(data);
  }
  setTouchDimAPI(data: FeatureFlagState): void {
    this.touchDimFeatApi.next(data);
  }
  setdiia253maintenanceAPI(data: FeatureFlagState): void {
    this.diia253maintenanceFeatApi.next(data);
  }
  setDynaDimmerAPI(data: FeatureFlagState): void {
    this.dynaDimmerFeatApi.next(data);
  }
  setDali102AddressingAPI(data: FeatureFlagState): void {
    this.dali102AddressingFeatApi.next(data);
  }
  setDali102and207ConfigurationAPI(data: FeatureFlagState): void {
    this.dali102and207ConfigurationFeatApi.next(data);
  }
  setAuxAPI(data: FeatureFlagState): void {
    this.auxFeatApi.next(data);
  }
  setLsiAPI(data: FeatureFlagState): void {
    this.lsiFeatApi.next(data);
  }
  setAocMultiChannelAPI(data: FeatureFlagState): void {
    this.aocmultichannelFeatApi.next(data);
  }
  setOtpAPI(data: FeatureFlagState): void {
    this.otpFeatApi.next(data);
  }

  setOtpSchemaVersion(version: string): void {
    this.otpSchemaVersion.next(version);
  }
  setDscAPI(data: FeatureFlagState): void {
    this.dscFeatApi.next(data);
  }
  setDscSchemaVersion(version: string): void {
    this.dscSchemaVersion.next(version);
  }
  setLowMainsLockAPI(data: FeatureFlagState): void {
    this.lowmainslockFeatApi.next(data);
  }
  setLowMainsLockSchemaVersion(version: string): void {
    this.lowmainslockSchemaVersion.next(version);
  }
  setAocMultiChannelSchemaVersion(version: string): void {
    this.aocmultichannelSchemaVersion.next(version);
  }
  setDali102AddressingSchemaVersion(version: string): void {
    this.dali102AddressingSchemaVersion.next(version);
  }
  setDali102and207ConfigurationSchemaVersion(version: string): void {
    this.dali102and207ConfigurationSchemaVersion.next(version);
  }
  setAuxSchemaVersion(version: string): void {
    this.auxSchemaVersion.next(version);
  }
  setLsiSchemaVersion(version: string): void {
    this.lsiSchemaVersion.next(version);
  }
}
